import { useState } from "react";
import {
  FaGithub, FaExternalLinkAlt, FaGooglePlay, FaAppStore,
  FaStar, FaTimes, FaChevronLeft, FaChevronRight,
} from "react-icons/fa";
import { MdRocketLaunch } from "react-icons/md";
import { projects } from "../data/projects";

const safeProjects = Array.isArray(projects) ? projects : [];
const CATEGORIES = ["All", "Mobile", "Web", "API"];

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const [tab, setTab] = useState("all");

  const byTab = tab === "products"
    ? safeProjects.filter((p) => p.type === "product")
    : tab === "projects"
    ? safeProjects.filter((p) => p.type === "project")
    : safeProjects;

  const filtered = filter === "All" ? byTab : byTab.filter((p) => p.category === filter);

  return (
    <section id="projects" className="bg-navy-950 section-padding">
      <div className="container-max">
        <div className="text-center mb-10">
          <span className="section-tag">What I've Built</span>
          <h2 className="section-title">Projects &amp; Products</h2>
          <p className="section-sub">
            Published software products generating real-world impact, alongside
            portfolio projects showcasing my technical range.
          </p>
        </div>

        {/* Type tabs */}
        <div className="flex justify-center gap-2 mb-6">
          {[
            { key: "all",      label: "All" },
            { key: "products", label: "Published Products" },
            { key: "projects", label: "Portfolio Projects" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
                tab === t.key
                  ? "bg-cyan-500 text-navy-950 border-cyan-500 shadow-glow"
                  : "bg-transparent border-navy-700 text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400"
              }`}>
              {t.key === "products" && <MdRocketLaunch size={14} />}
              {t.label}
            </button>
          ))}
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {CATEGORIES.map((cat) => (
            <button key={cat} onClick={() => setFilter(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                filter === cat
                  ? "bg-navy-700 text-cyan-400 border-cyan-500/50"
                  : "bg-transparent border-navy-800 text-slate-500 hover:border-navy-600 hover:text-slate-400"
              }`}>
              {cat}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
          {filtered.length === 0 && (
            <p className="text-slate-500 text-sm col-span-3 text-center py-10">
              No items match this filter.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Carousel + Lightbox card ──────────────────────────────────────────────────
function ProjectCard({ project }) {
  const { name, description, tech, category, type, images = [], github, live, playStore, appStore } = project;
  const isProduct = type === "product";
  const hasImages = images.length > 0;

  const [current, setCurrent]   = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [lbIndex, setLbIndex]   = useState(0);

  const prev = (e, setter, len, idx) => { e.stopPropagation(); setter((idx - 1 + len) % len); };
  const next = (e, setter, len, idx) => { e.stopPropagation(); setter((idx + 1) % len); };

  const openLightbox = (i) => { setLbIndex(i); setLightbox(true); };

  return (
    <>
      {/* ── Lightbox ── */}
      {lightbox && hasImages && (
        <div className="fixed inset-0 z-50 bg-navy-950/95 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}>
          <button className="absolute top-5 right-5 text-slate-400 hover:text-white transition-colors"
            onClick={() => setLightbox(false)}>
            <FaTimes size={22} />
          </button>

          {/* Lightbox image */}
          <div className="relative flex items-center justify-center w-full max-w-3xl"
            onClick={(e) => e.stopPropagation()}>
            <img
              src={`/assets/images/${images[lbIndex]}`}
              alt={`${name} screenshot ${lbIndex + 1}`}
              className="max-h-[80vh] rounded-2xl shadow-glow-lg object-contain w-full"
            />

            {/* Lightbox nav */}
            {images.length > 1 && (
              <>
                <button onClick={(e) => prev(e, setLbIndex, images.length, lbIndex)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full
                             bg-navy-900/80 border border-navy-700 text-slate-300 hover:text-cyan-400
                             hover:border-cyan-500/50 flex items-center justify-center transition-colors">
                  <FaChevronLeft size={14} />
                </button>
                <button onClick={(e) => next(e, setLbIndex, images.length, lbIndex)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full
                             bg-navy-900/80 border border-navy-700 text-slate-300 hover:text-cyan-400
                             hover:border-cyan-500/50 flex items-center justify-center transition-colors">
                  <FaChevronRight size={14} />
                </button>
              </>
            )}
          </div>

          {/* Lightbox dots */}
          {images.length > 1 && (
            <div className="absolute bottom-6 flex gap-2">
              {images.map((_, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setLbIndex(i); }}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === lbIndex ? "bg-cyan-400" : "bg-slate-600 hover:bg-slate-400"
                  }`} />
              ))}
            </div>
          )}

          <p className="absolute bottom-10 text-slate-400 text-xs">
            {name} &nbsp;{images.length > 1 ? `· ${lbIndex + 1} / ${images.length}` : ""}
          </p>
        </div>
      )}

      {/* ── Card ── */}
      <div className={`rounded-2xl overflow-hidden flex flex-col transition-all duration-300 group
                       ${isProduct
                         ? "bg-navy-800 border border-cyan-500/30 hover:border-cyan-400/60 hover:shadow-glow-lg"
                         : "bg-navy-800 border border-navy-700 hover:border-cyan-500/40 hover:shadow-glow"}`}>

        {/* Thumbnail / Carousel */}
        <div className="relative overflow-hidden" style={{ height: hasImages ? "220px" : "80px" }}>
          {hasImages ? (
            <>
              {/* Image */}
              <img
                src={`/assets/images/${images[current]}`}
                alt={`${name} screenshot ${current + 1}`}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105 cursor-pointer"
                onClick={() => openLightbox(current)}
              />

              {/* Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent pointer-events-none" />

              {/* Expand hint */}
              <div className="absolute inset-0 bg-navy-950/0 group-hover:bg-navy-950/20 transition-all duration-300
                              flex items-center justify-center pointer-events-none">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300
                                 text-xs font-semibold text-white bg-navy-900/80 backdrop-blur-sm
                                 px-3 py-1.5 rounded-full border border-navy-700">
                  Click to expand
                </span>
              </div>

              {/* Carousel arrows (only if multiple images) */}
              {images.length > 1 && (
                <>
                  <button onClick={(e) => prev(e, setCurrent, images.length, current)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full
                               bg-navy-900/80 border border-navy-700 text-slate-300 hover:text-cyan-400
                               flex items-center justify-center transition-colors z-10">
                    <FaChevronLeft size={11} />
                  </button>
                  <button onClick={(e) => next(e, setCurrent, images.length, current)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full
                               bg-navy-900/80 border border-navy-700 text-slate-300 hover:text-cyan-400
                               flex items-center justify-center transition-colors z-10">
                    <FaChevronRight size={11} />
                  </button>

                  {/* Dot indicators */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                    {images.map((_, i) => (
                      <button key={i}
                        onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                          i === current ? "bg-cyan-400" : "bg-slate-500 hover:bg-slate-300"
                        }`} />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className={`w-full h-full flex items-center justify-center
                             ${isProduct ? "bg-cyan-500/5" : "bg-navy-900"}`}>
              <span className="text-2xl font-black text-navy-700 tracking-widest">{category}</span>
            </div>
          )}

          {/* Published / Project badge */}
          <div className={`absolute top-3 left-3 flex items-center gap-1 text-xs font-bold
                            px-2.5 py-1 rounded-full border backdrop-blur-sm pointer-events-none
                            ${isProduct
                              ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/40"
                              : "bg-navy-900/80 text-slate-400 border-navy-600"}`}>
            {isProduct && <FaStar size={9} />}
            {isProduct ? "Published" : "Project"}
          </div>

          {/* Category badge */}
          <div className="absolute top-3 right-3 pointer-events-none">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm
                             bg-navy-900/80 text-cyan-400 border border-cyan-500/20">
              {category}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col flex-1">
          <h3 className={`font-bold text-sm leading-snug mb-2 ${isProduct ? "text-white" : "text-slate-200"}`}>
            {name}
          </h3>
          <p className="text-xs text-slate-400 leading-relaxed mb-4 flex-1">{description}</p>

          {/* Tech */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {tech.map((t) => (
              <span key={t} className="text-xs px-2 py-0.5 rounded bg-navy-900 border border-navy-700 text-slate-400">
                {t}
              </span>
            ))}
          </div>

          {/* Links */}
          <div className="flex flex-wrap gap-2 mt-auto">
            {github && (
              <a href={github} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-navy-600
                           text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors">
                <FaGithub size={12} /> GitHub
              </a>
            )}
            {live && (
              <a href={live} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-navy-600
                           text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors">
                <FaExternalLinkAlt size={10} /> Live
              </a>
            )}
            {playStore && (
              <a href={playStore} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-navy-600
                           text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors">
                <FaGooglePlay size={11} /> Play Store
              </a>
            )}
            {appStore && (
              <a href={appStore} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-navy-600
                           text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors">
                <FaAppStore size={11} /> App Store
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
}